import { useMemo } from "react";
import { useCurrentUser } from "./useCurrentUser";
import { useFetchSchools } from "./useFetchSchools";
import { useMyEnrollments } from "./useMyEnrollments";
import { useGetSectionsByIds } from "./useGetSectionsByIds";
import { useGetSchoolsByIds } from "./useGetSchoolsByIds";

interface MySchool {
  id: string;
  schoolName?: string;
  parentID?: string;
  path?: string;
  shardID?: string;
  contractTTL?: number;
}

/**
 * The current user's schools — the single school source for all roles:
 *   - **District admin** (effectiveRole "districtadmin", which also covers a
 *     superadmin with an active district): all schools in their district.
 *   - **Everyone else** (school staff, coach): schools they're directly
 *     enrolled in (SCHOOL-scoped enrollments) plus the parent schools of the
 *     sections they're enrolled in (SECTION-scoped).
 */
export const useMySchools = (): {
  schools: MySchool[];
  schoolIds: string[];
  isLoading: boolean;
} => {
  const currentUser = useCurrentUser();
  const isDistrictAdmin =
    currentUser?.effectiveRole === "districtadmin" ||
    !!currentUser?.hasEnteredADistrict;

  // District admin: all schools in the district. (Disabled for other roles by
  // passing no district id.)
  const { data: districtSchools = [], isLoading: districtLoading } =
    useFetchSchools(isDistrictAdmin ? currentUser?.districtId : undefined);

  // Everyone else: schools derived from enrollments.
  const { enrollments, loading: enrollmentsLoading } = useMyEnrollments();

  const directSchoolIds = useMemo(
    () =>
      enrollments
        .filter((enrollment) => enrollment.scope === "SCHOOL")
        .map((enrollment) => enrollment.entityId),
    [enrollments],
  );

  const sectionIds = useMemo(
    () => [
      ...new Set(
        enrollments
          .filter((enrollment) => enrollment.scope === "SECTION")
          .map((enrollment) => enrollment.entityId),
      ),
    ],
    [enrollments],
  );

  // Resolve section → parent school. Skipped for admins (they use the district
  // list above).
  const { data: sections, isLoading: sectionsLoading } = useGetSectionsByIds(
    isDistrictAdmin ? [] : sectionIds,
  );

  const enrolledSchoolIds = useMemo(() => {
    const parentSchoolIds = (sections ?? [])
      .map((section) => section.parentID)
      .filter(Boolean);
    return [...new Set([...directSchoolIds, ...parentSchoolIds])];
  }, [directSchoolIds, sections]);

  const { schools: enrolledSchools, isLoading: schoolsLoading } =
    useGetSchoolsByIds(isDistrictAdmin ? [] : enrolledSchoolIds);

  const schools = isDistrictAdmin
    ? (districtSchools as MySchool[])
    : (enrolledSchools ?? []);

  const schoolIds = useMemo(
    () => schools.map((school) => school.id).filter(Boolean),
    [schools],
  );

  return {
    schools,
    schoolIds,
    isLoading: isDistrictAdmin
      ? districtLoading
      : enrollmentsLoading || sectionsLoading || schoolsLoading,
  };
};
